import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { purchaseAPI } from '@/lib/api';
import { QtyDiscrepancyDialog } from '@/components/QtyDiscrepancyDialog';
import { EntityLink } from '@/components/EntityLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  FileText, Loader2, RefreshCw, CheckCircle, XCircle, Eye, Plus,
  Package, Send, Upload, Download, Paperclip, AlertTriangle, Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function PurchaseRequestsPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const initialQ = searchParams.get('q') || '';
  const initialSupplier = searchParams.get('supplier_id') || '';
  const [prs, setPrs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState(initialStatus || '');
  const [supplierFilter, setSupplierFilter] = useState(initialSupplier || '');
  const [searchQ, setSearchQ] = useState(initialQ || '');
  const [searchInput, setSearchInput] = useState(initialQ || '');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [detailPR, setDetailPR] = useState(null);
  const [detailReceiptStatus, setDetailReceiptStatus] = useState(null);
  const [detailReceiptLoading, setDetailReceiptLoading] = useState(false);
  const [receiptReloadKey, setReceiptReloadKey] = useState(0);
  const [rcptBusy, setRcptBusy] = useState(false);
  const [editRcpt, setEditRcpt] = useState(null); // {receiptId, itemId, itemName, currentQty, value}
  const [reverseRcpt, setReverseRcpt] = useState(null); // {receiptId, label}
  const [approvalAction, setApprovalAction] = useState(null);
  const [approvalRemarks, setApprovalRemarks] = useState('');
  const [quotationFile, setQuotationFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  // PO Generation
  const [poDialog, setPoDialog] = useState(null);
  const [poNumber, setPoNumber] = useState('');
  const [poRemarks, setPoRemarks] = useState('');
  const [poPdfFile, setPoPdfFile] = useState(null);
  const [readingPoPdf, setReadingPoPdf] = useState(false);
  // Edit PO number
  const [editPoDialog, setEditPoDialog] = useState(null); // { pr, suggested }
  const [editPoNumber, setEditPoNumber] = useState('');
  const [editPoReason, setEditPoReason] = useState('');
  // PO Send
  const [sendPoDialog, setSendPoDialog] = useState(null);
  const [sendPoMessage, setSendPoMessage] = useState('');
  const [sendPoEmail, setSendPoEmail] = useState('');
  const [sendPoCc, setSendPoCc] = useState('');
  // Discrepancy dialog
  const [discrepancyOpen, setDiscrepancyOpen] = useState(false);
  const [discrepancyPR, setDiscrepancyPR] = useState(null);
  // File upload
  const [uploadingFile, setUploadingFile] = useState('');
  // Cancel PO dialog
  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [cancelEmail, setCancelEmail] = useState('');
  const [cancelCc, setCancelCc] = useState('');
  const [cancelBody, setCancelBody] = useState('');
  // Short Close PO dialog
  const [shortCloseDialog, setShortCloseDialog] = useState(null);
  const [shortCloseRemarks, setShortCloseRemarks] = useState('');
  const [shortCloseEmail, setShortCloseEmail] = useState('');
  const [shortCloseCc, setShortCloseCc] = useState('');
  const [shortCloseBody, setShortCloseBody] = useState('');

  const isManager = isAdmin || user?.app_roles?.supplier_mgmt === 'manager';

  useEffect(() => { loadPRs(); }, [statusFilter, supplierFilter, searchQ, dateFrom, dateTo]);

  // Load supplier list once for the supplier filter dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await purchaseAPI.getSuppliers();
        const list = res.data?.suppliers || res.data || [];
        setSuppliers(list);
      } catch { /* non-critical */ }
    })();
  }, []);

  // Debounce search input → searchQ (350ms)
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Deep-link: ?open=<pr_id> auto-opens that PO's detail dialog (used by report drill-downs)
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    (async () => {
      try {
        const res = await purchaseAPI.getPurchaseRequest(openId);
        if (res.data) setDetailPR(res.data);
      } catch {
        toast.error('Could not open the requested purchase order');
      }
    })();
  }, [searchParams]);

  // Load receipt status whenever a PO has been sent (so partial receipts can be inspected)
  useEffect(() => {
    if (!detailPR) {
      setDetailReceiptStatus(null);
      return;
    }
    const status = detailPR.status;
    if (!['po_sent', 'partially_received', 'received', 'short_closed'].includes(status)) {
      setDetailReceiptStatus(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setDetailReceiptLoading(true);
      try {
        const res = await purchaseAPI.getPOReceiptStatus(detailPR.id);
        if (!cancelled) setDetailReceiptStatus(res.data);
      } catch {
        if (!cancelled) setDetailReceiptStatus(null);
      } finally {
        if (!cancelled) setDetailReceiptLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailPR, receiptReloadKey]);

  const handleReverseReceipt = async () => {
    if (!reverseRcpt) return;
    setRcptBusy(true);
    try {
      const res = await purchaseAPI.reversePurchaseReceipt(reverseRcpt.receiptId);
      toast.success(`Receipt reversed. PO is now ${(res.data.po_status || '').replace(/_/g, ' ')}`);
      setReverseRcpt(null);
      setReceiptReloadKey(k => k + 1);
      loadPRs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse receipt');
    } finally { setRcptBusy(false); }
  };

  const handleEditReceiptQty = async () => {
    if (!editRcpt) return;
    const newQty = parseFloat(editRcpt.value);
    if (isNaN(newQty) || newQty < 0) { toast.error('Enter a valid quantity (0 or more)'); return; }
    setRcptBusy(true);
    try {
      const res = await purchaseAPI.editReceiptQty(editRcpt.receiptId, { item_id: editRcpt.itemId, new_qty: newQty });
      toast.success(`Received qty corrected. PO is now ${(res.data.po_status || '').replace(/_/g, ' ')}`);
      setEditRcpt(null);
      setReceiptReloadKey(k => k + 1);
      loadPRs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to correct quantity');
    } finally { setRcptBusy(false); }
  };

  const loadPRs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter && statusFilter !== 'all_statuses') params.status = statusFilter;
      if (supplierFilter && supplierFilter !== 'all_suppliers') params.supplier_id = supplierFilter;
      if (searchQ) params.q = searchQ;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await purchaseAPI.listPurchaseRequests(params);
      setPrs(res.data.purchase_requests || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load purchase requests'); }
    finally { setLoading(false); }
  };

  const handleApproval = async () => {
    if (!approvalAction) return;
    setProcessing(true);
    try {
      if (approvalAction.action === 'approve' && quotationFile) {
        // Use approve-with-quote endpoint when quotation is attached
        const formData = new FormData();
        formData.append('action', 'approve');
        formData.append('remarks', approvalRemarks);
        formData.append('quotation_file', quotationFile);
        await purchaseAPI.approvePurchaseRequestWithQuote(approvalAction.pr_id, formData);
      } else {
        await purchaseAPI.approvePurchaseRequest(approvalAction.pr_id, {
          action: approvalAction.action, remarks: approvalRemarks,
        });
      }
      toast.success(`Purchase request ${approvalAction.action}d` + (quotationFile ? ' with quotation attached' : ''));
      setApprovalAction(null);
      setApprovalRemarks('');
      setQuotationFile(null);
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to process'); }
    finally { setProcessing(false); }
  };

  const handleReadPoPdf = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPoPdfFile(file);
    setReadingPoPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await purchaseAPI.extractPONumber(formData);
      if (res.data?.po_number) {
        setPoNumber(res.data.po_number);
        toast.success(`PO number read from PDF: ${res.data.po_number}`);
      } else {
        toast.warning('Could not read a PO number from this PDF — please type it in');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to read PDF'); }
    finally { setReadingPoPdf(false); }
  };

  const handleGeneratePO = async () => {
    if (!poDialog || !poNumber.trim()) { toast.error('Enter PO number'); return; }
    setProcessing(true);
    try {
      await purchaseAPI.generatePO(poDialog.id, { po_number: poNumber, remarks: poRemarks });
      if (poPdfFile) {
        const formData = new FormData();
        formData.append('file', poPdfFile);
        try {
          await purchaseAPI.uploadPOFile(poDialog.id, formData);
        } catch {
          toast.error(`PO ${poNumber} generated, but attaching the PDF failed — use Attach PO to retry`);
          setPoDialog(null); setPoNumber(''); setPoRemarks(''); setPoPdfFile(null); loadPRs();
          return;
        }
      }
      toast.success(`PO ${poNumber} generated${poPdfFile ? ' with PO document attached' : ''}`);
      setPoDialog(null);
      setPoNumber('');
      setPoRemarks('');
      setPoPdfFile(null);
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate PO'); }
    finally { setProcessing(false); }
  };

  const openEditPoDialog = (pr, suggested = '') => {
    setEditPoDialog({ pr, suggested });
    setEditPoNumber(suggested || pr.po_number || '');
    setEditPoReason('');
  };

  const handleUpdatePoNumber = async () => {
    if (!editPoDialog || !editPoNumber.trim()) { toast.error('Enter PO number'); return; }
    setProcessing(true);
    try {
      const res = await purchaseAPI.updatePONumber(editPoDialog.pr.id, {
        po_number: editPoNumber.trim(), reason: editPoReason,
      });
      toast.success(res.data?.message || 'PO number updated');
      setEditPoDialog(null);
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update PO number'); }
    finally { setProcessing(false); }
  };

  const handleUploadPO = async (pr, e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingFile(pr.id);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await purchaseAPI.uploadPOFile(pr.id, formData);
      toast.success('PO file uploaded');
      const detected = res.data?.detected_po_number;
      if (detected && detected !== (pr.po_number || '')) {
        toast.info(`PDF shows PO ${detected} — review the PO number`);
        openEditPoDialog(pr, detected);
      }
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to upload'); }
    finally { setUploadingFile(''); }
  };

  const openCancelDialog = (pr) => {
    setCancelDialog(pr);
    setCancelRemarks('');
    setCancelEmail(pr.supplier_email || pr.po_sent_to_email || '');
    setCancelCc(user?.email || '');
    setCancelBody('');
  };

  const handleCancelPO = async () => {
    if (!cancelDialog || !cancelRemarks.trim()) { toast.error('Cancellation reason is required'); return; }
    setProcessing(true);
    try {
      const res = await purchaseAPI.cancelPO(cancelDialog.id, {
        remarks: cancelRemarks.trim(),
        supplier_email: cancelEmail,
        cc_emails: cancelCc,
        email_body: cancelBody,
      });
      toast.success(res.data.message || 'PO cancelled');
      setCancelDialog(null);
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to cancel PO'); }
    finally { setProcessing(false); }
  };

  const openShortCloseDialog = (pr) => {
    setShortCloseDialog(pr);
    setShortCloseRemarks('');
    setShortCloseEmail(pr.supplier_email || pr.po_sent_to_email || '');
    setShortCloseCc(user?.email || '');
    setShortCloseBody('');
  };

  const handleShortClosePO = async () => {
    if (!shortCloseDialog || !shortCloseRemarks.trim()) { toast.error('Short-close reason is required'); return; }
    setProcessing(true);
    try {
      const res = await purchaseAPI.shortClosePO(shortCloseDialog.id, {
        remarks: shortCloseRemarks.trim(),
        supplier_email: shortCloseEmail,
        cc_emails: shortCloseCc,
        email_body: shortCloseBody,
      });
      toast.success(res.data.message || 'PO short-closed');
      setShortCloseDialog(null);
      loadPRs();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to short-close PO'); }
    finally { setProcessing(false); }
  };



  const handleSendPO = async () => {
    if (!sendPoDialog) return;
    setProcessing(true);
    try {
      const res = await purchaseAPI.sendPO(sendPoDialog.id, {
        supplier_email: sendPoEmail,
        message: sendPoMessage,
        cc_emails: sendPoCc,
      });
      if (res.data.is_amendment) {
        toast.success(`Amended PO (Rev ${res.data.amendment_count}) sent to ${res.data.supplier_email}`);
      } else {
        toast.success(res.data.message || 'PO sent');
      }
      setSendPoDialog(null);
      setSendPoMessage('');
      setSendPoEmail('');
      setSendPoCc('');
      loadPRs();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send PO';
      toast.error(detail, { duration: 8000 });
    }
    finally { setProcessing(false); }
  };

  const handleDownloadQuote = async (pr) => {
    if (!pr.quote_file_id) return;
    try {
      const res = await purchaseAPI.downloadQuoteFile(pr.id, pr.quote_file_id);
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = pr.quote_file_name || 'quotation';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download quotation'); }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending_approval: { label: 'Pending Approval', className: 'bg-amber-50 text-amber-700 border-amber-200' },
      approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' },
      po_generated: { label: 'PO Generated', className: 'bg-blue-50 text-blue-700 border-blue-200' },
      po_sent: { label: 'PO Sent', className: 'bg-purple-50 text-purple-700 border-purple-200' },
      partially_received: { label: 'Partially Received', className: 'bg-orange-50 text-orange-700 border-orange-200' },
      received: { label: 'Received', className: 'bg-teal-50 text-teal-700 border-teal-200' },
      cancelled: { label: 'Cancelled', className: 'bg-red-50 text-red-700 border-red-200' },
      short_closed: { label: 'Short Closed', className: 'bg-amber-50 text-amber-700 border-amber-200' },
    };
    const s = map[status] || { label: status, className: '' };
    return <Badge variant="outline" className={`text-[10px] ${s.className}`}>{s.label}</Badge>;
  };

  return (
    <div className="space-y-4" data-testid="purchase-requests-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl font-bold tracking-tight flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" /> Purchase Requests
          </h1>
          <p className="text-xs text-muted-foreground">{total} purchase requests</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            placeholder="Search PR/PO #, item name or code..."
            className="w-64 h-8 text-xs"
            data-testid="pr-search-input"
          />
          <div className="flex items-center gap-1" title="Filter by created date">
            <Input
              type="date"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              className="w-36 h-8 text-xs"
              data-testid="pr-date-from"
            />
            <span className="text-[10px] text-muted-foreground">to</span>
            <Input
              type="date"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              className="w-36 h-8 text-xs"
              data-testid="pr-date-to"
            />
          </div>
          <Select value={supplierFilter} onValueChange={setSupplierFilter}>
            <SelectTrigger className="w-52 h-8 text-xs" data-testid="pr-supplier-filter">
              <SelectValue placeholder="All Suppliers" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all_suppliers">All Suppliers</SelectItem>
              {suppliers.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.supplier_name || s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-8 text-xs" data-testid="pr-status-filter">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_statuses">All Statuses</SelectItem>
              <SelectItem value="pending_approval">Pending Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="po_generated">PO Generated</SelectItem>
              <SelectItem value="po_sent">PO Sent</SelectItem>
              <SelectItem value="partially_received">Partially Received</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          {(searchQ || (supplierFilter && supplierFilter !== 'all_suppliers') || (statusFilter && statusFilter !== 'all_statuses') || dateFrom || dateTo) && (
            <Button size="sm" variant="ghost" className="h-8 text-xs"
              onClick={() => { setSearchInput(''); setSearchQ(''); setSupplierFilter(''); setStatusFilter(''); setDateFrom(''); setDateTo(''); }}
              data-testid="pr-clear-filters-btn">
              Clear
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={loadPRs} data-testid="refresh-pr-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => navigate('/purchase/requests/new')} data-testid="new-pr-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> New PR
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-32 text-sm text-muted-foreground">Loading...</div>
      ) : prs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="w-10 h-10 text-slate-300 mb-3" />
            <p className="text-sm font-medium text-slate-500">No purchase requests found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {prs.map(pr => (
            <Card key={pr.id} data-testid={`pr-card-${pr.id}`}>
              <CardContent className="pt-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-semibold text-sm">{pr.pr_number}</span>
                      {getStatusBadge(pr.status)}
                      {pr.po_number && <Badge variant="outline" className="text-[10px] bg-slate-50">PO: {pr.po_number}</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Supplier: <span className="text-foreground font-medium">{pr.supplier_name || 'N/A'}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {pr.items?.length || 0} items | Est. Value: <span className="font-semibold text-foreground">Rs. {(pr.total_value_before_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                      {pr.quote_file_name && <span className="ml-2 text-blue-600 inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" /> Quote attached</span>}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      Created by {pr.created_by_name} on {new Date(pr.created_at).toLocaleDateString()}
                      {pr.approved_by_name && ` | Approved: ${pr.approved_by_name}`}
                      {pr.po_file_name && ` | File: ${pr.po_file_name}`}
                      {pr.po_sent_to_email && ` | Sent to: ${pr.po_sent_to_email}`}
                      {pr.po_amendment_count > 0 && ` | Rev ${pr.po_amendment_count}`}
                    </p>
                  </div>
                  <div className="flex gap-1.5 flex-wrap justify-end">
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDetailPR(pr)} data-testid={`view-pr-${pr.id}`}>
                      <Eye className="w-3 h-3 mr-1" /> View
                    </Button>
                    {isManager && pr.status === 'pending_approval' && (
                      <>
                        <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => { setApprovalAction({ pr_id: pr.id, action: 'approve' }); setApprovalRemarks(''); }}
                          data-testid={`approve-pr-${pr.id}`}>
                          <CheckCircle className="w-3 h-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" className="h-7 text-xs"
                          onClick={() => { setApprovalAction({ pr_id: pr.id, action: 'reject' }); setApprovalRemarks(''); }}
                          data-testid={`reject-pr-${pr.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Reject
                        </Button>
                      </>
                    )}
                    {pr.status === 'approved' && (
                      <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                        onClick={() => { setPoDialog(pr); setPoNumber(''); setPoRemarks(''); setPoPdfFile(null); }}
                        data-testid={`gen-po-${pr.id}`}>
                        <Package className="w-3 h-3 mr-1" /> Generate PO
                      </Button>
                    )}
                    {(pr.status === 'po_generated' || pr.status === 'approved' || pr.status === 'po_sent') && (
                      <>
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => handleUploadPO(pr, e)} />
                          <Button size="sm" variant="outline" className="h-7 text-xs pointer-events-none" asChild>
                            <span>
                              {uploadingFile === pr.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                              {pr.status === 'po_sent' ? 'Re-attach PO' : 'Attach PO'}
                            </span>
                          </Button>
                        </label>
                      </>
                    )}
                    {pr.po_number && !['cancelled'].includes(pr.status) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        onClick={() => openEditPoDialog(pr)}
                        data-testid={`edit-po-number-${pr.id}`}>
                        <Pencil className="w-3 h-3 mr-1" /> Edit PO #
                      </Button>
                    )}
                    {(pr.status === 'po_generated' || pr.status === 'po_sent') && (
                      <Button size="sm" className="h-7 text-xs bg-purple-600 hover:bg-purple-700"
                        onClick={() => { setSendPoDialog(pr); setSendPoEmail(''); setSendPoMessage(''); setSendPoCc(''); }}
                        data-testid={`send-po-${pr.id}`}>
                        <Send className="w-3 h-3 mr-1" /> {pr.status === 'po_sent' ? 'Resend Amended PO' : 'Send PO'}
                      </Button>
                    )}
                    {['po_generated', 'po_sent', 'partially_received', 'received'].includes(pr.status) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                        onClick={() => { setDiscrepancyPR(pr); setDiscrepancyOpen(true); }}
                        data-testid={`report-disc-${pr.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> Short/Excess
                      </Button>
                    )}
                    {!['cancelled', 'received', 'closed', 'short_closed'].includes(pr.status) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => openCancelDialog(pr)}
                        data-testid={`cancel-po-${pr.id}`}>
                        <XCircle className="w-3 h-3 mr-1" /> Cancel
                      </Button>
                    )}
                    {['po_sent', 'partially_received'].includes(pr.status) && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-600 hover:bg-amber-50"
                        onClick={() => openShortCloseDialog(pr)}
                        data-testid={`short-close-po-${pr.id}`}>
                        <AlertTriangle className="w-3 h-3 mr-1" /> Short Close
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detailPR} onOpenChange={() => setDetailPR(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-sm">{detailPR?.pr_number}{detailPR?.supplier_name ? ` - ${detailPR.supplier_name}` : ''}</DialogTitle>
          </DialogHeader>
          {detailPR && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><span className="text-muted-foreground">Status:</span> {getStatusBadge(detailPR.status)}</div>
                <div><span className="text-muted-foreground">Total:</span> <span className="font-semibold">Rs. {(detailPR.total_value_before_tax || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                {detailPR.po_number && <div><span className="text-muted-foreground">PO#:</span> {detailPR.po_number}</div>}
                {detailPR.created_at && <div><span className="text-muted-foreground">Created:</span> {new Date(detailPR.created_at).toLocaleDateString()} by {detailPR.created_by_name}</div>}
              </div>
              {detailPR.requires_admin_approval && detailPR.status === 'pending_approval' && (
                <div className="p-2.5 bg-amber-50 border border-amber-300 rounded-lg" data-testid="admin-approval-required-banner">
                  <p className="text-xs font-medium text-amber-800 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Admin approval required — item(s) priced above the last purchase price
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {(detailPR.price_flags || []).map((f, fi) => (
                      <li key={fi} className="text-[11px] text-amber-700">
                        {f.unique_id ? `${f.unique_id} — ` : ''}{f.item_name}: entered ₹{f.entered_rate?.toLocaleString('en-IN')} vs last ₹{f.previous_rate?.toLocaleString('en-IN')}{f.previous_po_number ? ` (${f.previous_po_number})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {detailPR.quote_file_name && (
                <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                  <Paperclip className="w-4 h-4 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-blue-800">Supplier Quotation</p>
                    <p className="text-[10px] text-blue-600 truncate">{detailPR.quote_file_name}</p>
                  </div>
                  <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100"
                    onClick={() => handleDownloadQuote(detailPR)} data-testid="download-quote-btn">
                    <Download className="w-3 h-3 mr-1" /> Download
                  </Button>
                </div>
              )}
              {detailPR.common_delivery_terms && <div className="text-xs"><span className="text-muted-foreground">Delivery:</span> {detailPR.common_delivery_terms}</div>}
              {detailPR.common_payment_terms && <div className="text-xs"><span className="text-muted-foreground">Payment:</span> {detailPR.common_payment_terms}</div>}
              {detailPR.common_documents_required && <div className="text-xs"><span className="text-muted-foreground">Docs Required:</span> {detailPR.common_documents_required}</div>}
              {detailPR.common_special_terms && <div className="text-xs"><span className="text-muted-foreground">Special:</span> {detailPR.common_special_terms}</div>}

              <div className="overflow-x-auto border rounded">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-2 font-medium">Item</th>
                      <th className="text-center p-2 font-medium">Qty</th>
                      {detailReceiptStatus && <th className="text-center p-2 font-medium">Received</th>}
                      {detailReceiptStatus && <th className="text-center p-2 font-medium">Pending</th>}
                      <th className="text-center p-2 font-medium">Rate</th>
                      <th className="text-center p-2 font-medium">GST%</th>
                      <th className="text-center p-2 font-medium">Total</th>
                      <th className="text-left p-2 font-medium">Delivery</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailPR.items || []).map((item, idx) => {
                      const rs = detailReceiptStatus?.items?.find(x => x.item_id === item.item_id);
                      const received = rs ? rs.received_qty : 0;
                      const pending = rs ? rs.pending_qty : 0;
                      const lineStatus = rs?.line_status || '';
                      return (
                      <tr key={idx} className="border-b" data-testid={`po-detail-item-row-${idx}`}>
                        <td className="p-2">
                          <div className="font-medium"><EntityLink type="item" id={item.item_id}>{item.item_name || 'Service Item'}</EntityLink></div>
                          <div className="text-muted-foreground"><EntityLink type="item" id={item.item_id} className="font-mono text-muted-foreground text-xs">{item.unique_id}</EntityLink></div>
                          {item.item_description && <div className="text-muted-foreground italic">{item.item_description}</div>}
                        </td>
                        <td className="p-2 text-center">{`${item.quantity} ${item.unit_of_measure || ''}`}</td>
                        {detailReceiptStatus && (
                          <td className="p-2 text-center font-mono text-emerald-700">{received}</td>
                        )}
                        {detailReceiptStatus && (
                          <td className="p-2 text-center">
                            <span className={`font-mono ${pending > 0 ? 'text-rose-700 font-medium' : 'text-emerald-700'}`}>{pending}</span>
                            {lineStatus && (
                              <span className={`ml-1 text-[9px] px-1 py-0 rounded border ${
                                lineStatus === 'complete' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                                lineStatus === 'partial' ? 'border-amber-300 text-amber-700 bg-amber-50' :
                                'border-slate-300 text-slate-600 bg-slate-50'
                              }`}>
                                {lineStatus === 'complete' ? 'Done' : lineStatus === 'partial' ? 'Partial' : 'Open'}
                              </span>
                            )}
                          </td>
                        )}
                        <td className="p-2 text-center">{(item.rate || 0).toFixed(2)}</td>
                        <td className="p-2 text-center">{item.gst_percent || 0}%</td>
                        <td className="p-2 text-center font-medium">{(item.line_total || 0).toFixed(2)}</td>
                        <td className="p-2 text-muted-foreground">{item.delivery_date || '-'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Receipt Status & Timeline (only shown for POs that have been sent) */}
              {detailReceiptLoading && (
                <p className="text-xs text-muted-foreground italic" data-testid="po-receipt-status-loading">Loading receipt status…</p>
              )}
              {detailReceiptStatus && (
                <div className="space-y-2" data-testid="po-receipt-status-section">
                  <div className="flex flex-wrap items-center gap-2 text-[11px]">
                    <span className="font-medium">Receipt status:</span>
                    <span className="px-2 py-0.5 rounded border border-slate-300 bg-slate-50">
                      Lines pending: <strong>{detailReceiptStatus.totals?.lines_pending || 0}</strong> / {detailReceiptStatus.totals?.lines_total || 0}
                    </span>
                    <span className="px-2 py-0.5 rounded border border-emerald-300 text-emerald-700 bg-emerald-50">
                      Received qty: <strong>{detailReceiptStatus.totals?.received_qty || 0}</strong>
                    </span>
                    <span className={`px-2 py-0.5 rounded border ${
                      (detailReceiptStatus.totals?.pending_qty || 0) > 0
                        ? 'border-rose-300 text-rose-700 bg-rose-50'
                        : 'border-emerald-300 text-emerald-700 bg-emerald-50'
                    }`}>
                      Pending qty: <strong>{detailReceiptStatus.totals?.pending_qty || 0}</strong>
                    </span>
                  </div>

                  {(detailReceiptStatus.receipts || []).length > 0 && (
                    <div className="border rounded overflow-hidden">
                      <p className="px-2 py-1.5 text-[11px] font-medium bg-muted/50 border-b">Receipt timeline ({detailReceiptStatus.receipts.length})</p>
                      <div className="max-h-[200px] overflow-y-auto">
                        {detailReceiptStatus.receipts.map((rec, ri) => (
                          <div key={rec.id || ri} className="px-2 py-1.5 border-b text-[11px]" data-testid={`po-receipt-entry-${ri}`}>
                            <div className="flex items-center justify-between">
                              <span className="font-medium">
                                Receipt #{ri + 1} — {rec.received_at?.slice(0, 10)} {rec.received_at?.slice(11, 16)}
                              </span>
                              <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">by {rec.received_by_name || '—'}</span>
                                {isAdmin && !['cancelled', 'closed', 'short_closed'].includes(detailPR.status) && (
                                  reverseRcpt?.receiptId === rec.id ? (
                                    <span className="flex items-center gap-1">
                                      <span className="text-red-600 text-[10px]">Reverse?</span>
                                      <Button size="sm" className="h-5 px-1.5 text-[10px] bg-red-600 hover:bg-red-700" onClick={handleReverseReceipt} disabled={rcptBusy} data-testid={`confirm-reverse-receipt-${ri}`}>
                                        {rcptBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
                                      </Button>
                                      <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]" onClick={() => setReverseRcpt(null)} disabled={rcptBusy}>No</Button>
                                    </span>
                                  ) : (
                                    <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px] text-red-600 hover:bg-red-50"
                                      onClick={() => { setEditRcpt(null); setReverseRcpt({ receiptId: rec.id, label: `Receipt #${ri + 1}` }); }}
                                      data-testid={`reverse-receipt-${ri}`}>
                                      <XCircle className="w-3 h-3 mr-0.5" /> Reverse
                                    </Button>
                                  )
                                )}
                              </div>
                            </div>
                            <ul className="ml-3 mt-0.5 space-y-0.5">
                              {(rec.items || []).map((ri2, j) => (
                                <li key={j} className="text-muted-foreground flex items-center gap-1.5 flex-wrap">
                                  <span className="font-mono text-foreground">{ri2.received_qty}</span> × {ri2.item_name || ri2.item_unique_id}
                                  {isAdmin && !['cancelled', 'closed', 'short_closed'].includes(detailPR.status) && ri2.item_id && (
                                    editRcpt?.receiptId === rec.id && editRcpt?.itemId === ri2.item_id ? (
                                      <span className="inline-flex items-center gap-1">
                                        <span className="text-foreground">→</span>
                                        <Input type="number" min="0" step="any" value={editRcpt.value}
                                          onChange={e => setEditRcpt(r => ({ ...r, value: e.target.value }))}
                                          className="h-6 w-20 text-[11px] px-1.5" autoFocus data-testid={`edit-receipt-qty-input-${ri}-${j}`} />
                                        <Button size="sm" className="h-6 px-2 text-[10px]" onClick={handleEditReceiptQty} disabled={rcptBusy} data-testid={`confirm-edit-receipt-qty-${ri}-${j}`}>
                                          {rcptBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => setEditRcpt(null)} disabled={rcptBusy}>Cancel</Button>
                                      </span>
                                    ) : (
                                      <button
                                        className="text-blue-600 hover:underline text-[10px]"
                                        onClick={() => { setReverseRcpt(null); setEditRcpt({ receiptId: rec.id, itemId: ri2.item_id, itemName: ri2.item_name || ri2.item_unique_id, currentQty: ri2.received_qty, value: String(ri2.received_qty) }); }}
                                        data-testid={`edit-receipt-qty-${ri}-${j}`}>
                                        Edit qty
                                      </button>
                                    )
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button variant="outline" size="sm" onClick={() => setDetailPR(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={!!approvalAction} onOpenChange={() => { setApprovalAction(null); setQuotationFile(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-sm">{approvalAction?.action === 'approve' ? 'Approve' : 'Reject'} PR</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Show existing quote attached during PR creation */}
            {approvalAction && (() => {
              const prData = prs.find(p => p.id === approvalAction.pr_id);
              if (prData?.quote_file_name) {
                return (
                  <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-lg">
                    <Paperclip className="w-4 h-4 text-blue-600 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-blue-800">Supplier Quotation (attached during PR creation)</p>
                      <p className="text-[10px] text-blue-600 truncate">{prData.quote_file_name}</p>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-100 shrink-0"
                      onClick={() => handleDownloadQuote(prData)} data-testid="approval-download-quote-btn">
                      <Download className="w-3 h-3 mr-1" /> View
                    </Button>
                  </div>
                );
              }
              return null;
            })()}
            {approvalAction?.action === 'approve' && (
              <div>
                <Label className="text-xs font-medium">Attach Supplier Quotation (Optional)</Label>
                <p className="text-[10px] text-muted-foreground mb-1.5">
                  The quotation will be sent to accounts in the approval email
                </p>
                <Input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={e => setQuotationFile(e.target.files?.[0] || null)}
                  className="h-8 text-xs"
                  data-testid="quotation-file-input"
                />
                {quotationFile && (
                  <div className="flex items-center justify-between mt-1.5 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    <span className="truncate max-w-[200px]">{quotationFile.name}</span>
                    <button onClick={() => setQuotationFile(null)} className="text-red-500 hover:text-red-700 ml-2">
                      <XCircle className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={approvalRemarks} onChange={e => setApprovalRemarks(e.target.value)} placeholder="Optional..." className="mt-1 text-xs" rows={3} data-testid="approval-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setApprovalAction(null); setQuotationFile(null); }}>Cancel</Button>
            <Button size="sm" variant={approvalAction?.action === 'approve' ? 'default' : 'destructive'} onClick={handleApproval} disabled={processing} data-testid="confirm-approval">
              {processing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}
              {approvalAction?.action === 'approve' ? (quotationFile ? 'Approve + Attach Quote' : 'Approve') : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Generate PO Dialog */}
      <Dialog open={!!poDialog} onOpenChange={() => { setPoDialog(null); setPoPdfFile(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm">Generate Purchase Order for {poDialog?.pr_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Attach PO PDF (reads the PO number automatically)</Label>
              <label className="mt-1 flex items-center gap-2 cursor-pointer">
                <input type="file" className="hidden" accept=".pdf" onChange={handleReadPoPdf} data-testid="po-pdf-read-input" />
                <Button size="sm" variant="outline" className="h-7 text-xs pointer-events-none" asChild>
                  <span>
                    {readingPoPdf ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Upload className="w-3 h-3 mr-1" />}
                    Choose PO PDF
                  </span>
                </Button>
                {poPdfFile && <span className="text-[10px] text-emerald-600 truncate" data-testid="po-pdf-name">{poPdfFile.name}</span>}
              </label>
            </div>
            <div>
              <Label className="text-xs">PO Number *</Label>
              <Input value={poNumber} onChange={e => setPoNumber(e.target.value)} className="h-8 text-xs" placeholder="e.g. BHAPL/26-27/10/412" data-testid="po-number-input" />
            </div>
            <div>
              <Label className="text-xs">Remarks</Label>
              <Textarea value={poRemarks} onChange={e => setPoRemarks(e.target.value)} className="text-xs" rows={2} placeholder="Optional..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { setPoDialog(null); setPoPdfFile(null); }}>Cancel</Button>
            <Button size="sm" onClick={handleGeneratePO} disabled={processing} data-testid="confirm-gen-po">
              {processing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Generate PO
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit PO Number Dialog */}
      <Dialog open={!!editPoDialog} onOpenChange={() => setEditPoDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm">Correct PO Number — {editPoDialog?.pr?.pr_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Current PO number: <span className="font-mono font-medium text-foreground">{editPoDialog?.pr?.po_number}</span>
            </p>
            {editPoDialog?.suggested && (
              <p className="text-xs text-blue-600" data-testid="po-number-suggestion">
                Read from attached PDF: <span className="font-mono font-medium">{editPoDialog.suggested}</span>
              </p>
            )}
            <div>
              <Label className="text-xs">New PO Number *</Label>
              <Input value={editPoNumber} onChange={e => setEditPoNumber(e.target.value)} className="h-8 text-xs" data-testid="edit-po-number-input" />
            </div>
            <div>
              <Label className="text-xs">Reason for correction</Label>
              <Textarea value={editPoReason} onChange={e => setEditPoReason(e.target.value)} className="text-xs" rows={2} placeholder="Optional..." data-testid="edit-po-reason-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setEditPoDialog(null)}>Cancel</Button>
            <Button size="sm" onClick={handleUpdatePoNumber} disabled={processing} data-testid="confirm-edit-po-number">
              {processing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} Save PO Number
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send PO Dialog */}
      <Dialog open={!!sendPoDialog} onOpenChange={() => setSendPoDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-sm">
              {sendPoDialog?.status === 'po_sent' ? `Resend Amended PO ${sendPoDialog?.po_number}` : `Send PO ${sendPoDialog?.po_number} to Supplier`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {sendPoDialog?.status === 'po_sent' && (
              <div className="p-2.5 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800">
                This PO was already sent to <strong>{sendPoDialog?.po_sent_to_email}</strong> on {sendPoDialog?.po_sent_at ? new Date(sendPoDialog.po_sent_at).toLocaleDateString() : 'N/A'}.
                Resending will mark it as an amended/revised PO.
                {sendPoDialog?.po_amendment_count > 0 && ` (Current revision: ${sendPoDialog.po_amendment_count})`}
              </div>
            )}
            <div>
              <Label className="text-xs">Supplier Email</Label>
              <Input value={sendPoEmail} onChange={e => setSendPoEmail(e.target.value)} className="h-8 text-xs" placeholder="Leave blank to use supplier's registered email" data-testid="send-po-email" />
            </div>
            <div>
              <Label className="text-xs">CC (optional)</Label>
              <Input value={sendPoCc} onChange={e => setSendPoCc(e.target.value)} className="h-8 text-xs" placeholder="Comma-separated emails to CC (e.g. user1@bhpl.co.in, user2@bhpl.co.in)" data-testid="send-po-cc" />
            </div>
            <div>
              <Label className="text-xs">Additional Message</Label>
              <Textarea value={sendPoMessage} onChange={e => setSendPoMessage(e.target.value)} className="text-xs" rows={3} placeholder="Any additional notes, drawing references..." data-testid="send-po-msg" />
            </div>
            {sendPoDialog?.po_file_name && (
              <p className="text-xs text-emerald-600">Attached file: {sendPoDialog.po_file_name}</p>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5" data-testid="send-po-ack-clause">
              <p className="text-[11px] font-semibold text-slate-700">Order acknowledgement (included in every PO email)</p>
              <p className="text-[11px] text-slate-500 leading-relaxed mt-1">
                The supplier shall confirm acceptance of this purchase order in writing (e-mail acceptable) within 5 working days,
                stating any deviation from the order (quantity, date, specification, price). Deviations are accepted only when agreed
                in writing by BHPL. If no response is received within this period, the order and these terms and conditions are deemed
                accepted without deviation. For orders carrying customer-specific requirements (clause XXVIII of annexure), written
                acknowledgement is mandatory before the supplier starts work.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setSendPoDialog(null)}>Cancel</Button>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700" onClick={handleSendPO} disabled={processing} data-testid="confirm-send-po">
              {processing && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />} <Send className="w-3.5 h-3.5 mr-1" /> {sendPoDialog?.status === 'po_sent' ? 'Resend Amended PO' : 'Send PO'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Qty Discrepancy Dialog */}
      <QtyDiscrepancyDialog
        open={discrepancyOpen}
        onClose={() => { setDiscrepancyOpen(false); setDiscrepancyPR(null); loadPRs(); }}
        sourceType="purchase_receipt"
        sourceId={discrepancyPR?.id || ''}
        sourceNumber={discrepancyPR?.po_number || discrepancyPR?.pr_number || ''}
        partyType="supplier"
        partyName={discrepancyPR?.supplier_name || ''}
        partyId={discrepancyPR?.supplier_id || ''}
        partyEmail={discrepancyPR?.supplier_email || ''}
        invoiceNumber=""
        godowns={[]}
        items={(discrepancyPR?.items || []).map(it => ({
          item_id: it.item_id || '',
          item_name: it.item_name || '',
          item_unique_id: it.unique_id || it.item_unique_id || '',
          expected_qty: it.quantity || 0,
          received_qty: it.received_qty || 0,
          unit_of_measure: it.unit_of_measure || 'Nos',
          godown_id: '',
        }))}
      />

      {/* Cancel PO Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={() => setCancelDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="cancel-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="w-5 h-5" />
              Cancel Purchase Order
            </DialogTitle>
          </DialogHeader>
          {cancelDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-xs font-medium">{cancelDialog.po_number || cancelDialog.pr_number}</p>
                <p className="text-[10px] text-muted-foreground">Supplier: {cancelDialog.supplier_name}</p>
                <p className="text-[10px] text-red-600 mt-1">This will cancel the PO and send a cancellation notification to the supplier.</p>
              </div>

              <div>
                <Label className="text-xs">Cancellation Reason *</Label>
                <textarea
                  value={cancelRemarks}
                  onChange={e => setCancelRemarks(e.target.value)}
                  className="w-full h-16 text-xs border rounded-md p-2 mt-1 resize-none"
                  placeholder="Reason for cancelling this PO..."
                  data-testid="cancel-po-remarks"
                />
              </div>

              <div>
                <Label className="text-xs">Supplier Email</Label>
                <Input
                  value={cancelEmail}
                  onChange={e => setCancelEmail(e.target.value)}
                  className="h-7 text-xs mt-1"
                  placeholder="supplier@example.com"
                  data-testid="cancel-po-email"
                />
              </div>

              <div>
                <Label className="text-xs">CC Emails (comma separated)</Label>
                <Input
                  value={cancelCc}
                  onChange={e => setCancelCc(e.target.value)}
                  className="h-7 text-xs mt-1"
                  placeholder="cc1@example.com, cc2@example.com"
                  data-testid="cancel-po-cc"
                />
              </div>

              <div>
                <Label className="text-xs">Additional Message (optional)</Label>
                <textarea
                  value={cancelBody}
                  onChange={e => setCancelBody(e.target.value)}
                  className="w-full h-16 text-xs border rounded-md p-2 mt-1 resize-none"
                  placeholder="Any additional message to include in the cancellation email..."
                  data-testid="cancel-po-body"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setCancelDialog(null)}>Go Back</Button>
            <Button size="sm" variant="destructive" onClick={handleCancelPO} disabled={processing || !cancelRemarks.trim()} data-testid="confirm-cancel-po">
              {processing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
              Cancel PO & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Short Close PO Dialog */}
      <Dialog open={!!shortCloseDialog} onOpenChange={() => setShortCloseDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="short-close-po-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />
              Short Close Purchase Order
            </DialogTitle>
          </DialogHeader>
          {shortCloseDialog && (
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs font-medium">{shortCloseDialog.po_number || shortCloseDialog.pr_number}</p>
                <p className="text-[10px] text-muted-foreground">Supplier: {shortCloseDialog.supplier_name}</p>
                <p className="text-[10px] text-amber-700 mt-1">This will close the PO with remaining balance not required. A notification will be sent to the supplier.</p>
              </div>

              {/* Items summary */}
              {shortCloseDialog.items?.length > 0 && (
                <div className="border rounded overflow-hidden">
                  <table className="w-full text-[10px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="p-1.5 text-left">Item</th>
                        <th className="p-1.5 text-right">Ordered</th>
                        <th className="p-1.5 text-right">Received</th>
                        <th className="p-1.5 text-right">Pending</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shortCloseDialog.items.map((item, idx) => {
                        const received = item.received_qty || 0;
                        const pending = Math.max(0, (item.quantity || 0) - received);
                        return (
                          <tr key={idx} className="border-t">
                            <td className="p-1.5">{item.item_name || item.unique_id}</td>
                            <td className="p-1.5 text-right font-mono">{item.quantity}</td>
                            <td className="p-1.5 text-right font-mono text-green-600">{received}</td>
                            <td className={`p-1.5 text-right font-mono ${pending > 0 ? 'text-red-600 font-medium' : 'text-green-600'}`}>{pending}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div>
                <Label className="text-xs">Short Close Reason *</Label>
                <textarea
                  value={shortCloseRemarks}
                  onChange={e => setShortCloseRemarks(e.target.value)}
                  className="w-full h-16 text-xs border rounded-md p-2 mt-1 resize-none"
                  placeholder="Reason for short-closing this PO (e.g., partial delivery accepted, remaining items no longer needed...)"
                  data-testid="short-close-po-remarks"
                />
              </div>

              <div>
                <Label className="text-xs">Supplier Email</Label>
                <Input
                  value={shortCloseEmail}
                  onChange={e => setShortCloseEmail(e.target.value)}
                  className="h-7 text-xs mt-1"
                  placeholder="supplier@example.com"
                  data-testid="short-close-po-email"
                />
              </div>

              <div>
                <Label className="text-xs">CC Emails (comma separated)</Label>
                <Input
                  value={shortCloseCc}
                  onChange={e => setShortCloseCc(e.target.value)}
                  className="h-7 text-xs mt-1"
                  placeholder="cc1@example.com, cc2@example.com"
                  data-testid="short-close-po-cc"
                />
              </div>

              <div>
                <Label className="text-xs">Additional Message (optional)</Label>
                <textarea
                  value={shortCloseBody}
                  onChange={e => setShortCloseBody(e.target.value)}
                  className="w-full h-14 text-xs border rounded-md p-2 mt-1 resize-none"
                  placeholder="Any additional message to include..."
                  data-testid="short-close-po-body"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShortCloseDialog(null)}>Go Back</Button>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleShortClosePO} disabled={processing || !shortCloseRemarks.trim()} data-testid="confirm-short-close-po">
              {processing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />}
              Short Close & Notify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
